/**
 * Property pickers label their options with the address alone, which stops
 * working the moment two properties share one. The dropdown then shows two
 * identical rows, and picking the wrong one silently yields an empty result —
 * the deals belong to the other property.
 *
 * Only colliding labels get a qualifier, so the common case stays clean. Type
 * and status usually tell the two apart; when even those match (a genuine
 * duplicate card), a trailing ordinal keeps every option selectable.
 */
type PropertyFacts = { type?: string | null; status?: string | null }

export function disambiguatePropertyLabels(
	titles: Map<string, string>,
	facts: Map<string, PropertyFacts>,
	t: (key: string) => string
): Map<string, string> {
	const byTitle = new Map<string, string[]>()
	for (const [id, title] of titles) {
		const ids = byTitle.get(title)
		if (ids) ids.push(id)
		else byTitle.set(title, [id])
	}

	const result = new Map<string, string>()
	for (const [title, ids] of byTitle) {
		if (ids.length === 1) {
			result.set(ids[0], title)
			continue
		}
		const qualified = ids.map((id) => {
			const f = facts.get(id)
			const parts = [title]
			if (f?.type) parts.push(t(`properties.types.${f.type}`))
			if (f?.status) parts.push(t(`properties.statuses.${f.status}`))
			return { id, label: parts.join(' · ') }
		})
		// Qualifiers can still collide — two duplicate cards of the same type and
		// status. Number those so each row remains individually selectable.
		const seen = new Map<string, number>()
		const totals = new Map<string, number>()
		for (const { label } of qualified) totals.set(label, (totals.get(label) ?? 0) + 1)
		for (const { id, label } of qualified) {
			if ((totals.get(label) ?? 0) === 1) {
				result.set(id, label)
				continue
			}
			const n = (seen.get(label) ?? 0) + 1
			seen.set(label, n)
			result.set(id, `${label} (${n})`)
		}
	}

	// Preserve the caller's original ordering rather than the grouped one.
	return new Map([...titles.keys()].map((id) => [id, result.get(id) ?? titles.get(id)!]))
}
